import React, { FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Button, Divider, Grid, Link, Paper, Stack, Tooltip, Typography } from '@mui/material'
import format from 'date-fns/format'
import { DataLoad, EntityHeader, HasSidebarLayout, InnerLink } from 'src/components'
import { DeletePromptModal } from 'src/components/sections/prompt'
import { usePromptSingleRequest, useDeletePromptRequest } from 'src/hooks/api'
import { useAddPopupMessage, useDialog, useDownloadingFile } from 'src/hooks/ui'
import { DialogNames, Entities } from 'src/enums'

const InfoRow: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Grid container spacing={{ xs: 0, sm: 1 }} mt={0.5}>
    <Grid item xs={12} sm={3}>
      <Typography color="text.secondary">{label}</Typography>
    </Grid>
    <Grid item xs={12} sm={9}>
      {children}
    </Grid>
  </Grid>
)

const PromptShow: FC = () => {
  document.title = 'CLīMIT Admin Panel - Prompt'
  const navigate = useNavigate()
  const params = useParams()
  const id = Number(params.id)
  const addMessage = useAddPopupMessage()
  const queryClient = useQueryClient()
  const { openDialog, closeDialog } = useDialog(DialogNames.DeletePrompt)
  const { downloadFile, isLoading: isDownloading } = useDownloadingFile()

  const { isInitialLoading, isError, error, data: prompt } = usePromptSingleRequest({ id })
  const deleteRequest = useDeletePromptRequest({ id })

  const responseCount = prompt?.responseCount || 0
  const hasResponses = responseCount > 0

  const onDelete = () => {
    deleteRequest.mutate(
      {},
      {
        onSuccess: () => {
          queryClient.invalidateQueries([Entities.Prompts])
          addMessage({ text: 'Prompt deleted', type: 'success' })
          closeDialog(() => navigate('/prompts'))
        },
        onError: ({ message: text }) => {
          addMessage({ text, type: 'error' })
          closeDialog()
        }
      }
    )
  }

  const downloadCsv = () => {
    downloadFile({
      urlPath: `v1/admin/prompts/${id}/responses/export`,
      queryParams: { clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      noDataMessage: 'No responses to export'
    })
  }

  return (
    <HasSidebarLayout>
      <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message || ''}>
        {prompt && (
          <>
            <EntityHeader title={prompt.title} onBack={() => navigate('/prompts')}>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" disabled={!hasResponses || isDownloading} onClick={downloadCsv}>
                  Download responses CSV
                </Button>
                <InnerLink to={`/prompts/${id}/edit`}>
                  <Button variant="outlined">Edit</Button>
                </InnerLink>
                <Tooltip title={hasResponses ? 'Cannot delete a prompt that has responses' : ''}>
                  <span>
                    <Button variant="contained" color="error" onClick={openDialog}>
                      Delete
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </EntityHeader>

            <Paper variant="outlined" sx={{ p: 3, mt: 2, maxWidth: 800 }}>
              <InfoRow label="Description">
                <Typography whiteSpace="pre-wrap">{prompt.description}</Typography>
              </InfoRow>
              <InfoRow label="Checkbox options">
                <Stack spacing={0.5}>
                  {prompt.checkboxOptions.map((option) => (
                    <Box key={option.key}>{option.label}</Box>
                  ))}
                </Stack>
              </InfoRow>
              {prompt.links && prompt.links.length > 0 && (
                <InfoRow label="Links">
                  <Stack spacing={0.5}>
                    {prompt.links.map((link) => (
                      <Link key={link.url} href={link.url} target="_blank" rel="noreferrer">
                        {link.name}
                      </Link>
                    ))}
                  </Stack>
                </InfoRow>
              )}
              <Divider sx={{ my: 2 }} />
              <InfoRow label="Start date">
                <Typography whiteSpace="pre-wrap">{prompt.startDate ? format(new Date(prompt.startDate), 'MMM d, yyyy') : '—'}</Typography>
              </InfoRow>
              <InfoRow label="Minimum app version">
                <Typography whiteSpace="pre-wrap">{prompt.minAppVersion || '—'}</Typography>
              </InfoRow>
              <InfoRow label="Skippable">
                <Typography whiteSpace="pre-wrap">{prompt.skippable ? 'Yes' : 'No'}</Typography>
              </InfoRow>
              <InfoRow label="Show to users who join later">
                <Typography whiteSpace="pre-wrap">{prompt.showNewUsers ? 'Yes' : 'No'}</Typography>
              </InfoRow>
              <InfoRow label="Responses">
                <Typography whiteSpace="pre-wrap">{responseCount}</Typography>
              </InfoRow>
            </Paper>

            <DeletePromptModal promptTitle={prompt.title} isLoading={deleteRequest.isLoading} onCancel={() => closeDialog()} onDelete={onDelete} />
          </>
        )}
      </DataLoad>
    </HasSidebarLayout>
  )
}

export default PromptShow
